export interface ServiceRate {
    serviceid: string; // Use in booking
    courier: string; // Service name with details (e.g., "Poslaju Same Day Pick up")
    courier_name?: string; // Clean courier name (e.g., "POSLAJU NATIONAL COURIER")
    courier_logo?: string; // Logo URL for the courier
    req_id?: string; // REQ_ID from rate check — required for order submission
    price: number; // RM amount
    eta_delivery: string; // Estimated delivery (e.g., "2-3 days")
    dropoff_point?: string; // Dropoff point
    pickup_point?: string; // Pickup point
}



export interface ParcelData {
    weight: number; // kg
    width?: number; // cm
    length?: number; // cm
    height?: number; // cm
    content: string; // Description (max 35 chars)
    value: number; // RM amount
    service_id: string; // From getRates() - MUST be snake_case
    courier_name?: string; // Courier display name (e.g., "Pos Laju")
    pick_name: string; // Sender name
    pick_company?: string; // Sender company
    pick_contact: string; // Sender phone
    pick_mobile?: string; // Sender mobile
    pick_addr1: string; // Sender address line 1
    pick_addr2?: string; // Sender address line 2
    pick_addr3?: string; // Sender address line 3
    pick_addr4?: string; // Sender address line 4
    pick_city: string; // Sender city
    pick_state: string; // Sender state
    pick_code: string; // Sender postcode
    pick_country?: string; // Sender country (default: MY)
    pick_point?: string; // Pickup point
    send_name: string; // Receiver name
    send_company?: string; // Receiver company
    send_contact: string; // Receiver phone
    send_mobile?: string; // Receiver mobile
    send_addr1: string; // Receiver address line 1
    send_addr2?: string; // Receiver address line 2
    send_addr3?: string; // Receiver address line 3
    send_addr4?: string; // Receiver address line 4
    send_city: string; // Receiver city
    send_state: string; // Receiver state
    send_code: string; // Receiver postcode
    send_email: string; // Receiver email
    send_country?: string; // Receiver country (default: MY)
    send_point?: string; // Delivery point
    collect_date: string; // YYYY-MM-DD format
    sms?: string; // SMS notification ('0' or '1')
    hs_code?: string; // Customs HS code
    REQ_ID?: string; // Request ID
    reference?: string; // Internal reference
    cod_enabled?: boolean; // COD enabled
    cod_amount?: number; // COD amount in RM
    order_id?: number; // Local order id — forwarded to proxy for shipping-cost capture
}

export interface BookingResponse {
    success: boolean;
    order_no: string; // Store in database
    awb?: string; // Airway bill number
    parcel_number?: string; // EasyParcel reference
    error_message?: string; // If failed
}

export interface ParcelStatus {
    order_no: string;
    awb: string; // Tracking number
    parcel_number: string; // EasyParcel internal ID
    ship_status: string; // e.g., "Schedule In Arrangement"
    awb_id_link: string; // PDF label URL - SEND TO CUSTOMER
    tracking_url?: string; // Public tracking URL for customers
    remarks?: string; // Error message or additional info
    message?: string; // Additional message from API
}

export interface TrackingResponse {
    awb: string;
    current_status: string;
    tracking_history: {
        timestamp: string;
        location: string;
        status: string;
    }[];
}

const TEST_URL = 'https://demo.connect.easyparcel.my/';
const LIVE_URL = 'https://connect.easyparcel.my/';

// Backend Express proxy URL (replaces Supabase Edge Function)
const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001');
const EDGE_FN_URL = `${API_URL}/api/courier/proxy`;

export class EasyParcelService {
    private mode: 'test' | 'live';
    private apiKey: string;
    private baseUrl: string;
    private maxRetries: number = 3;
    private retryDelay: number = process.env.NODE_ENV === 'test' ? 0 : 1000; // 0ms for tests

    constructor(mode?: 'test' | 'live') {
        // Determine mode from argument or env (checking standard Process env)
        const envMode = process.env.REACT_APP_EASY_PARCEL_MODE || 'test';
        this.mode = mode || (envMode === 'live' ? 'live' : 'test');

        this.baseUrl = this.mode === 'live' ? LIVE_URL : TEST_URL;

        // Get API Key
        if (this.mode === 'live') {
            this.apiKey = process.env.REACT_APP_EASY_PARCEL_API_KEY_LIVE || '';
        } else {
            this.apiKey = process.env.REACT_APP_EASY_PARCEL_API_KEY_TEST || 'EP-TEST-API-KEY';
        }
    }

    // Allow setting API Key manually (e.g. from UI settings)
    public setApiKey(key: string) {
        this.apiKey = key;
    }

    public setMode(mode: 'test' | 'live') {
        this.mode = mode;
        this.baseUrl = mode === 'live' ? LIVE_URL : TEST_URL;
    }

    private async request(action: string, body: any, retryCount = 0): Promise<any> {
        const startTime = Date.now();

        // Build payload for the Edge Function proxy
        const proxyPayload = {
            action,
            apiKey: this.apiKey,
            mode: this.mode,
            ...body
        };

        // Log Request
        if (process.env.REACT_APP_EASY_PARCEL_LOG_REQUESTS !== 'false') {
            console.log(`[EasyParcel ${this.mode.toUpperCase()}] Edge Function Request:`, {
                action,
                url: EDGE_FN_URL,
                body
            });
        }

        try {
            // Call Supabase Edge Function (server-side proxy to EasyParcel)
            const response = await fetch(EDGE_FN_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(proxyPayload),
            });

            const endTime = Date.now();
            const executionTime = endTime - startTime;

            // Handle HTTP Errors
            if (!response.ok) {
                const errText = await response.text();
                throw new Error(`HTTP ${response.status}: ${errText}`);
            }

            const data = await response.json();

            // Log Response
            if (process.env.REACT_APP_EASY_PARCEL_LOG_REQUESTS !== 'false') {
                console.log(`[EasyParcel ${this.mode.toUpperCase()}] Response (${executionTime}ms):`, {
                    status: data.api_status || data.status,
                    action
                });
            }

            // Check API-level errors - EasyParcel can return api_status or status
            const isSuccess = data.api_status === 'Success' || data.status === 'Success';

            if (!isSuccess) {
                const errorMsg = data.error_remark || data.remark || data.error || data.message || 'Unknown API error';
                console.error(`[EasyParcel ${this.mode.toUpperCase()}] API Error:`, errorMsg);
                throw new Error(errorMsg);
            }

            return data;
        } catch (error: any) {
            const endTime = Date.now();
            const executionTime = endTime - startTime;

            console.error(`[EasyParcel ${this.mode.toUpperCase()}] Request failed (${executionTime}ms):`, {
                action,
                error: error.message,
                retryCount
            });

            // Retry logic for network errors or Server Errors (5xx)
            if (retryCount < this.maxRetries && (error.message.includes('fetch') || error.message.includes('HTTP 5'))) {
                console.log(`[EasyParcel] Retrying... (${retryCount + 1}/${this.maxRetries})`);
                await new Promise(resolve => setTimeout(resolve, this.retryDelay));
                return this.request(action, body, retryCount + 1);
            }

            throw error;
        }
    }

    async getRates(params: {
        pickCode: string;
        sendCode: string;
        weight: number;
        pickState?: string;
        sendState?: string;
    }): Promise<ServiceRate[]> {
        const bulkPayload = [{
            pick_code: params.pickCode,
            pick_state: params.pickState || '',
            send_code: params.sendCode,
            send_state: params.sendState || '',
            weight: params.weight,
            pick_country: 'MY',
            send_country: 'MY'
        }];
        const response = await this.request('EPRateCheckingBulk', { bulk: bulkPayload });

        console.log('[EasyParcel] Full response:', response);

        // EasyParcel can return api_status or status
        const isSuccess = response.api_status === 'Success' || response.status === 'Success';

        console.log('[EasyParcel] Is success?', isSuccess);

        if (!isSuccess) {
            console.warn('[EasyParcel] getRates failed:', response.error || response.message);
            return [];
        }

        // EasyParcel returns rates in result[0].rates array
        const resultData = response.result || [];
        const ratesData = resultData[0]?.rates || [];

        console.log('[EasyParcel] Result data:', resultData);
        console.log('[EasyParcel] Rates data:', ratesData);
        console.log('[EasyParcel] Rates count:', ratesData.length);

        const rates: ServiceRate[] = ratesData.map((rate: any) => ({
            serviceid: rate.service_id,
            courier: rate.service_name || rate.courier_name,
            courier_name: rate.courier_name,
            courier_logo: rate.courier_logo,
            req_id: resultData[0]?.REQ_ID || resultData[0]?.req_id || '',
            price: parseFloat(rate.price),
            eta_delivery: rate.delivery_period || 'N/A',
            dropoff_point: rate.dropoff_point,
            pickup_point: rate.pickup_point
        }));


        console.log('[EasyParcel] Parsed rates:', rates);

        return rates;
    }

    async getBulkRates(paramsArray: {
        pickCode: string;
        sendCode: string;
        weight: number;
        pickState?: string;
        sendState?: string;
    }[]): Promise<ServiceRate[][]> {
        const bulkPayload = paramsArray.map(p => ({
            pick_code: p.pickCode,
            pick_state: p.pickState || '',
            send_code: p.sendCode,
            send_state: p.sendState || '',
            weight: p.weight,
            pick_country: 'MY',
            send_country: 'MY'
        }));

        console.log('[EasyParcel] getBulkRates Payload:', bulkPayload);

        const response = await this.request('EPRateCheckingBulk', { bulk: bulkPayload });

        console.log('[EasyParcel] getBulkRates Full response:', response);

        const isSuccess = response.api_status === 'Success' || response.status === 'Success';
        if (!isSuccess) {
            console.warn('[EasyParcel] getBulkRates failed:', response.error || response.message);
            return [];
        }

        const resultData = response.result || [];
        
        return resultData.map((res: any) => {
            const ratesData = res.rates || [];
            const reqId = res.REQ_ID || res.req_id || '';
            return ratesData.map((rate: any) => ({
                serviceid: rate.service_id,
                courier: rate.service_name || rate.courier_name,
                courier_name: rate.courier_name,
                courier_logo: rate.courier_logo,
                req_id: reqId,
                price: parseFloat(rate.price),
                eta_delivery: rate.delivery_period || 'N/A',
                dropoff_point: rate.dropoff_point,
                pickup_point: rate.pickup_point
            }));
        });
    }

    async bookParcel(orderData: ParcelData): Promise<BookingResponse> {
        // Structure for bulk order. Even for single, we use bulk array.
        const bulk = [{
            weight: orderData.weight,
            width: orderData.width,
            length: orderData.length,
            height: orderData.height,
            content: orderData.content,
            value: orderData.value,
            service_id: orderData.service_id, // ✅ snake_case
            pick_name: orderData.pick_name, // ✅ snake_case
            pick_company: orderData.pick_company,
            pick_contact: orderData.pick_contact,
            pick_mobile: orderData.pick_mobile,
            pick_addr1: orderData.pick_addr1,
            pick_addr2: orderData.pick_addr2,
            pick_addr3: orderData.pick_addr3,
            pick_addr4: orderData.pick_addr4,
            pick_city: orderData.pick_city,
            pick_state: orderData.pick_state,
            pick_code: orderData.pick_code,
            pick_country: orderData.pick_country || 'MY',
            pick_point: orderData.pick_point,
            send_name: orderData.send_name, // ✅ snake_case
            send_company: orderData.send_company,
            send_contact: orderData.send_contact,
            send_mobile: orderData.send_mobile,
            send_addr1: orderData.send_addr1,
            send_addr2: orderData.send_addr2,
            send_addr3: orderData.send_addr3,
            send_addr4: orderData.send_addr4,
            send_city: orderData.send_city,
            send_state: orderData.send_state,
            send_code: orderData.send_code,
            send_country: orderData.send_country || 'MY',
            send_email: orderData.send_email, // ✅ snake_case
            send_point: orderData.send_point,
            collect_date: orderData.collect_date, // ✅ snake_case
            sms: orderData.sms,
            hs_code: orderData.hs_code,
            REQ_ID: orderData.REQ_ID,
            reference: orderData.reference,
            cod_enabled: orderData.cod_enabled, // ✅ snake_case
            cod_amount: orderData.cod_amount // ✅ snake_case
        }];

        // 🔍 SERVICE LAYER LOGGING - Exact Data Sent to API
        console.log('');
        console.log('🌐 [EasyParcel Service] Preparing API Request...');
        console.log('📡 Endpoint: EPSubmitOrderBulk');
        console.log('📦 Bulk Array (First Item):');
        console.log('');
        console.log('  🔑 Field Name Validation:');
        console.log('    ✓ service_id (snake_case):', bulk[0].service_id);
        console.log('    ✓ pick_name (snake_case):', bulk[0].pick_name);
        console.log('    ✓ pick_contact (snake_case):', bulk[0].pick_contact);
        console.log('    ✓ pick_mobile (snake_case):', bulk[0].pick_mobile);
        console.log('    ✓ send_name (snake_case):', bulk[0].send_name);
        console.log('    ✓ send_contact (snake_case):', bulk[0].send_contact);
        console.log('    ✓ send_mobile (snake_case):', bulk[0].send_mobile);
        console.log('    ✓ send_email (snake_case):', bulk[0].send_email);
        console.log('    ✓ collect_date (snake_case):', bulk[0].collect_date);
        console.log('');
        console.log('  📞 Phone Number Validation:');
        console.log('    Pick Contact:', bulk[0].pick_contact,
            bulk[0].pick_contact?.startsWith('0') ? '✅' : '❌ INVALID!');
        console.log('    Pick Mobile:', bulk[0].pick_mobile,
            bulk[0].pick_mobile?.startsWith('0') ? '✅' : '❌ INVALID!');
        console.log('    Send Contact:', bulk[0].send_contact,
            bulk[0].send_contact?.startsWith('0') ? '✅' : '❌ INVALID!');
        console.log('    Send Mobile:', bulk[0].send_mobile,
            bulk[0].send_mobile?.startsWith('0') ? '✅' : '❌ INVALID!');
        console.log('');
        console.log('  📋 Complete Bulk Item:');
        console.log(JSON.stringify(bulk[0], null, 2));
        console.log('');

        console.log('🚀 [EasyParcel Service] Sending request to API...');
        const response = await this.request('EPSubmitOrderBulk', {
            bulk: bulk,
            // Forward local order_id so the edge proxy can lock the returned
            // shipping cost onto orders.shipping_cost (P&L Fulfilment Cost).
            order_id: orderData.order_id,
        });

        console.log('[EasyParcel] Booking full response:', response);

        // Check specifically for order success
        const result = response.result || [];

        console.log('[EasyParcel] Result array:', result);
        console.log('[EasyParcel] Result length:', result.length);

        if (result.length > 0) {
            const order = result[0];
            console.log('[EasyParcel] First order:', order);
            console.log('[EasyParcel] Order status:', order.status);

            // 🔍 DEBUG: Log ALL fields to see what's available
            console.log('🔍 [EasyParcel] Complete order object:');
            console.log('  - status:', order.status);
            console.log('  - order_number:', order.order_number);
            console.log('  - awb:', order.awb);
            console.log('  - parcel_number:', order.parcel_number);
            console.log('  - remarks:', order.remarks);
            console.log('  - message:', order.message);
            console.log('  - All keys:', Object.keys(order));

            if (order.status?.toLowerCase() === 'success') {
                const bookingResult = {
                    success: true,
                    order_no: order.order_number || '',
                    awb: order.awb || '', // May be empty in demo/test mode
                    parcel_number: order.parcel_number || ''
                };

                console.log('✅ [EasyParcel] Returning booking result:', bookingResult);

                return bookingResult;
            } else {
                const errorMsg = order.remarks || order.message || 'Booking failed';
                console.error('[EasyParcel] Booking error:', errorMsg);
                return {
                    success: false,
                    order_no: '',
                    error_message: errorMsg
                };
            }
        }

        console.log('[EasyParcel] No result found, returning error');
        return {
            success: false,
            order_no: '',
            error_message: response.error_remark || 'Unknown error'
        };
    }

    async bookBulkParcels(ordersDataArray: ParcelData[]): Promise<BookingResponse[]> {
        const bulk = ordersDataArray.map(orderData => ({
            weight: orderData.weight,
            width: orderData.width || 0,
            length: orderData.length || 0,
            height: orderData.height || 0,
            content: orderData.content,
            value: orderData.value,
            service_id: orderData.service_id,
            pick_name: orderData.pick_name,
            pick_company: orderData.pick_company,
            pick_contact: orderData.pick_contact,
            pick_mobile: orderData.pick_mobile,
            pick_addr1: orderData.pick_addr1,
            pick_addr2: orderData.pick_addr2,
            pick_addr3: orderData.pick_addr3,
            pick_addr4: orderData.pick_addr4,
            pick_city: orderData.pick_city,
            pick_state: orderData.pick_state,
            pick_code: orderData.pick_code,
            pick_country: orderData.pick_country || 'MY',
            pick_point: orderData.pick_point,
            send_name: orderData.send_name,
            send_company: orderData.send_company,
            send_contact: orderData.send_contact,
            send_mobile: orderData.send_mobile,
            send_addr1: orderData.send_addr1,
            send_addr2: orderData.send_addr2,
            send_addr3: orderData.send_addr3,
            send_addr4: orderData.send_addr4,
            send_city: orderData.send_city,
            send_state: orderData.send_state,
            send_code: orderData.send_code,
            send_country: orderData.send_country || 'MY',
            send_email: orderData.send_email,
            send_point: orderData.send_point,
            collect_date: orderData.collect_date,
            sms: orderData.sms || '0',
            hs_code: orderData.hs_code,
            REQ_ID: orderData.REQ_ID,
            reference: orderData.reference,
            cod_enabled: orderData.cod_enabled,
            cod_amount: orderData.cod_amount
        }));

        console.log(`🚀 [EasyParcel Service] Sending ${bulk.length} parcels in array to EPSubmitOrderBulk...`);
        const response = await this.request('EPSubmitOrderBulk', {
            bulk: bulk
        });

        console.log('[EasyParcel] Bulk Booking full response:', response);

        const result = response.result || [];
        
        return result.map((order: any, index: number) => {
            if (order.status?.toLowerCase() === 'success') {
                return {
                    success: true,
                    order_no: order.order_number || '',
                    awb: order.awb || '',
                    parcel_number: order.parcel_number || ''
                };
            } else {
                return {
                    success: false,
                    order_no: '',
                    error_message: order.remarks || order.message || 'Booking failed'
                };
            }
        });
    }

    async payOrder(orderNo: string): Promise<{ success: boolean; message: string; awb_link?: string; awb?: string; tracking_url?: string }> {
        try {
            const response = await this.request('EPPayOrderBulk', {
                bulk: [{ order_no: orderNo }]
            });

            console.log('[EasyParcel] payOrder full response:', response);

            // Check API status
            const isSuccess = response.api_status === 'Success';
            const resultList = response.result || [];

            console.log('[EasyParcel] Payment API status:', response.api_status);
            console.log('[EasyParcel] Payment result list:', resultList);

            // Should be at least one result
            if (isSuccess && resultList.length > 0) {
                const result = resultList[0];

                console.log('[EasyParcel] Payment result:', result);
                console.log('[EasyParcel] Payment result.status:', result.status);
                console.log('[EasyParcel] Payment result.remark:', result.remark);
                console.log('[EasyParcel] Payment result.messagenow:', result.messagenow);
                console.log('[EasyParcel] Payment result.parcel:', result.parcel);

                // Extract AWB link and tracking URL from parcel array
                let awbLink = '';
                let awb = '';
                let trackingUrl = '';

                if (result.parcel && result.parcel.length > 0) {
                    const parcelData = result.parcel[0];
                    awbLink = parcelData.awb_id_link || '';
                    awb = parcelData.awb || '';

                    // Generate tracking URL if AWB is available
                    if (awb) {
                        trackingUrl = `https://track.easyparcel.com/?track_id=${awb}`;
                    }

                    console.log('[EasyParcel] Extracted AWB Link:', awbLink);
                    console.log('[EasyParcel] Extracted AWB:', awb);
                    console.log('[EasyParcel] Generated Tracking URL:', trackingUrl);
                }

                // Check for insufficient credit error
                if (result.messagenow && result.messagenow.toLowerCase().includes('insufficient')) {
                    console.error('[EasyParcel] Payment FAILED: Insufficient Credit');
                    return {
                        success: false,
                        message: 'Insufficient Credit - Please top up your EasyParcel account'
                    };
                }

                // Check if payment is done (success message in messagenow)
                const isPaymentDone = result.messagenow &&
                    (result.messagenow.toLowerCase().includes('done') ||
                        result.messagenow.toLowerCase().includes('success') ||
                        result.messagenow.toLowerCase().includes('paid'));

                // Check normal status OR payment done message
                if (result.status === 'Success' || isPaymentDone) {
                    return {
                        success: true,
                        message: result.messagenow || result.remark || result.message || 'Payment successful',
                        awb_link: awbLink,
                        awb: awb,
                        tracking_url: trackingUrl
                    };
                }

                // If no clear success, return the message
                return {
                    success: false,
                    message: result.messagenow || result.remark || result.message || 'Payment failed'
                };
            }

            console.warn('[EasyParcel] Payment failed - no valid result');
            return {
                success: false,
                message: response.error_remark || 'Payment failed'
            };
        } catch (error: any) {
            console.error('[EasyParcel] Payment error:', error);
            return {
                success: false,
                message: error.message || 'Payment request failed'
            };
        }
    }

    async getParcelStatus(orderNo: string): Promise<ParcelStatus | null> {
        const response = await this.request('EPParcelStatusBulk', {
            bulk: [{ order_no: orderNo }]
        });

        console.log('[EasyParcel] getParcelStatus full response:', response);

        const result = response.result || [];
        if (result.length > 0) {
            const statusData = result[0];

            console.log('[EasyParcel] Status data:', statusData);
            console.log('[EasyParcel] Status:', statusData.status);
            console.log('[EasyParcel] Remarks:', statusData.remarks);
            console.log('[EasyParcel] Message:', statusData.message);
            console.log('[EasyParcel] AWB:', statusData.awb);

            // Generate tracking URL if AWB is available
            let trackingUrl = '';
            if (statusData.awb) {
                trackingUrl = `https://track.easyparcel.com/?track_id=${statusData.awb}`;
            }

            return {
                order_no: statusData.order_number,
                awb: statusData.awb,
                parcel_number: statusData.parcel_number,
                ship_status: statusData.status, // or ship_status
                awb_id_link: statusData.awb_id_link,
                tracking_url: trackingUrl,
                remarks: statusData.remarks, // Error message
                message: statusData.message  // Additional info
            };
        }
        return null;
    }

    async getTracking(awb: string): Promise<TrackingResponse | null> {
        const response = await this.request('EPTracking', {
            order_no: awb
        });

        const result = response.result || [];
        if (result.length > 0) {
            const trackData = result[0];
            const history = (trackData.details || []).map((h: any) => ({
                timestamp: h.date + ' ' + h.time,
                location: h.location,
                status: h.process
            }));

            return {
                awb: trackData.awb,
                current_status: trackData.status,
                tracking_history: history
            };
        }
        return null;
    }
}

export const easyParcelService = new EasyParcelService();
